import * as React from 'react';
import { RadioGroup, RadioGroupItem } from '../primitives/radio-group';
import { cn } from '../../lib/utils';

export interface RadioRowOption<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Sits under the label — what the option means. */
  description?: React.ReactNode;
  /**
   * Sits at the end of the label row — the option's current setting, so an
   * unselected option still says what switching back would give you.
   */
  summary?: React.ReactNode;
  /** Revealed under the label only while this option is selected. */
  content?: React.ReactNode;
  disabled?: boolean;
  testId?: string;
}

export interface RadioRowsProps<T extends string> {
  name: string;
  options: RadioRowOption<T>[];
  value?: T;
  onChange?: (_value: T) => void;
  disabled?: boolean;
  error?: string;
  'aria-label'?: string;
  className?: string;
}

/**
 * A grouped list of choices, each able to carry its own configuration.
 *
 * Selection is shown by the radio dot plus a rule down the left edge — the
 * row is not filled. A tinted panel put the chosen option in direct
 * competition with the page's primary action, and made a form of two such
 * groups read as two coloured blocks, which is what this replaces.
 *
 * Expanded configuration sits on the sunken surface rather than inheriting a
 * tint, so it reads as part of the row without restating the selection.
 */
export function RadioRows<T extends string>({
  name,
  options,
  value,
  onChange,
  disabled,
  error,
  className,
  ...aria
}: RadioRowsProps<T>) {
  return (
    <div className={className}>
      <RadioGroup
        value={value}
        onValueChange={(next) => onChange?.(next as T)}
        disabled={disabled}
        aria-label={aria['aria-label']}
        aria-invalid={error ? true : undefined}
        className={cn(
          'gap-0 overflow-hidden rounded-lg border bg-card',
          error ? 'border-destructive' : 'border-border',
        )}
      >
        {options.map((option) => {
          const selected = option.value === value;
          const isDisabled = disabled || option.disabled;
          const id = `${name}-${option.value}`;
          return (
            <div
              key={option.value}
              data-testid={option.testId}
              data-state={selected ? 'checked' : 'unchecked'}
              className={cn(
                'relative border-t border-border/60 px-4 py-3.5 first:border-t-0',
                !isDisabled && 'cursor-pointer',
                !isDisabled && !selected && 'hover:bg-muted/40',
              )}
              onClick={(event) => {
                if (isDisabled) return;
                // Let the label and the radio handle their own activation,
                // otherwise the click is counted twice.
                if ((event.target as HTMLElement).closest('label, [role="radio"]')) return;
                onChange?.(option.value);
              }}
            >
              {selected && (
                <span
                  aria-hidden
                  className="absolute inset-y-2.5 left-0 w-0.5 rounded-full bg-primary"
                />
              )}
              <div className="flex items-center gap-3">
                <RadioGroupItem id={id} value={option.value} disabled={isDisabled} />
                <label
                  htmlFor={id}
                  className={cn(
                    'text-sm font-medium',
                    isDisabled ? 'text-muted-foreground' : 'cursor-pointer text-foreground',
                  )}
                >
                  {option.label}
                </label>
                {option.summary && (
                  <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
                    {option.summary}
                  </span>
                )}
              </div>

              {option.description && (
                <p className="ml-7 mt-1 text-xs text-muted-foreground">{option.description}</p>
              )}

              {selected && option.content && (
                <div className="ml-7 mt-3 rounded-md border border-border/60 bg-muted/40 p-3.5">
                  {option.content}
                </div>
              )}
            </div>
          );
        })}
      </RadioGroup>
      {error && <p className="mt-1.5 text-sm text-destructive">{error}</p>}
    </div>
  );
}
